import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronRight, FaCheck, FaTimes, FaEllipsisH, FaSort, FaSortUp, FaSortDown, FaEnvelope, FaShieldAlt } from 'react-icons/fa';
import RoleBadge from '../../../components/Program/RoleBadge';
import StatusPill from '../../../components/Program/StatusPill';
import ClubRoleBadge from '../../../components/Program/ClubRoleBadge';
import { ROLE_DEFINITIONS, ROLE_CATEGORIES, CATEGORY_ORDER, getRolesByCategory } from '../../../constants/programRoles';
import styles from './UnifiedPeopleTable.module.css';

const TYPE_CONFIG = {
  'club-management': { label: 'Club Staff', icon: '🏢', color: '#8b5cf6' },
  'staff': { label: 'Staff', icon: '👔', color: '#3b82f6' },
  'member': { label: 'Member', icon: '👤', color: '#22c55e' },
  'applicant': { label: 'Applicant', icon: '📝', color: '#f59e0b' },
  'waitlist': { label: 'Waitlist', icon: '⏳', color: '#6b7280' },
  'follower': { label: 'Follower', icon: '👁️', color: '#ec4899' }
};

// Action Menu Component
const ActionMenu = ({ person, onAction, isRegistrationEnabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAction = (action) => {
    onAction(action, person);
    setIsOpen(false);
  };

  const getMenuItems = () => {
    switch (person.type) {
      case 'club-management':
        return [{ label: 'View Profile', icon: '👤', action: 'view' }];
      
      case 'member':
      case 'staff':
        const items = [
          { label: 'Change Role', icon: '🔄', action: 'changeRole' },
          { label: 'Change Status', icon: '📊', action: 'changeStatus' },
        ];
        if (person.status === 'Enrolled') {
          items.push(
            { label: 'Withdraw', icon: '↩️', action: 'withdraw', variant: 'warning' },
            { label: 'Suspend', icon: '⏸️', action: 'suspend', variant: 'danger' }
          );
        }
        items.push({ label: 'Remove', icon: '🗑️', action: 'remove', variant: 'danger' });
        return items;
      
      case 'applicant':
        return [
          { label: 'Approve', icon: '✅', action: 'approve', variant: 'success' },
          { label: 'Decline', icon: '❌', action: 'decline', variant: 'danger' },
        ];
      
      case 'waitlist':
        if (isRegistrationEnabled) {
          return person.canApplyDespiteRestrictions ? 
            [{ label: 'Remove from Waitlist', icon: '🗑️', action: 'remove', variant: 'danger' }] :
            [
              { label: 'Allow Application', icon: '✅', action: 'allowApplication', variant: 'success' },
              { label: 'Remove', icon: '🗑️', action: 'remove', variant: 'danger' }
            ];
        }
        return [
          { label: 'Approve for Membership', icon: '✅', action: 'directApproval', variant: 'success' },
          { label: 'Remove', icon: '🗑️', action: 'remove', variant: 'danger' }
        ];
      
      case 'follower':
        return [
          { label: 'Invite to Apply', icon: '📩', action: 'invite' },
        ];
      
      default:
        return [];
    }
  };

  const menuItems = getMenuItems();
  if (menuItems.length === 0) return <span className={styles.noActions}>—</span>;

  return (
    <div className={styles.actionMenu} ref={menuRef}>
      <button 
        className={styles.actionMenuBtn}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaEllipsisH />
      </button>
      {isOpen && (
        <div className={styles.actionMenuDropdown}>
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              className={`${styles.actionMenuItem} ${item.variant ? styles[`menuItem${item.variant.charAt(0).toUpperCase() + item.variant.slice(1)}`] : ''}`}
              onClick={() => handleAction(item.action)}
            >
              <span className={styles.menuItemIcon}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Quick Action Buttons (for common actions)
const QuickActions = ({ person, onAction, isRegistrationEnabled }) => {
  switch (person.type) {
    case 'applicant':
      return (
        <div className={styles.quickActions}>
          <button 
            className={`${styles.quickBtn} ${styles.quickBtnSuccess}`}
            onClick={() => onAction('approve', person)}
            title="Approve"
          >
            <FaCheck />
          </button>
          <button 
            className={`${styles.quickBtn} ${styles.quickBtnDanger}`}
            onClick={() => onAction('decline', person)}
            title="Decline"
          >
            <FaTimes />
          </button>
        </div>
      );
    
    case 'waitlist':
      if (!isRegistrationEnabled) {
        return (
          <button 
            className={`${styles.quickBtn} ${styles.quickBtnSuccess}`}
            onClick={() => onAction('directApproval', person)}
            title="Approve for Membership"
          >
            <FaCheck />
          </button>
        );
      }
      if (!person.canApplyDespiteRestrictions) {
        return (
          <button 
            className={styles.allowBtn}
            onClick={() => onAction('allowApplication', person)}
          >
            Allow Application
          </button>
        );
      }
      return <span className={styles.canApplyBadge}>✓ Can Apply</span>;
    
    default:
      return null;
  }
};

const UnifiedPeopleTable = ({
  data = [],
  counts = {},
  filters,
  setFilters,
  filterOptions = {},
  isLoading,
  selectedIds = [],
  selectedCount = 0,
  toggleSelection,
  selectAll,
  clearSelection,
  isSelected,
  onRefresh,
  onChangeRole,
  onChangeStatus,
  onRemove,
  onWithdraw,
  onSuspend,
  onApprove,
  onDecline,
  onAllowApplication,
  onDirectApproval,
  currentUserPermissions = [],
  isRegistrationEnabled = false,
  teams = []
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [roleModalUser, setRoleModalUser] = useState(null);
  const [statusModalUser, setStatusModalUser] = useState(null);

  // Build a map of userId → teamName for showing team allocation on each person
  const memberTeamMap = useMemo(() => {
    const map = {};
    if (!teams || teams.length === 0) return map;

    teams.forEach((team) => {
      const teamName = team.name || 'Unknown Team';

      if (team.players && Array.isArray(team.players)) {
        team.players.forEach((player) => {
          if (typeof player === 'string') {
            map[player] = teamName;
          } else {
            if (player.userId) map[player.userId] = teamName;
            if (player._id) map[player._id] = teamName;
            if (player.user?._id) map[player.user._id] = teamName;
          }
        });
      }

      if (team.coach) {
        if (typeof team.coach === 'string') {
          map[team.coach] = teamName;
        } else {
          if (team.coach._id) map[team.coach._id] = teamName;
          if (team.coach.userId) map[team.coach.userId] = teamName;
        }
      }

      if (team.assistantCoaches && Array.isArray(team.assistantCoaches)) {
        team.assistantCoaches.forEach((ac) => {
          if (typeof ac === 'string') {
            map[ac] = teamName;
          } else {
            if (ac._id) map[ac._id] = teamName;
            if (ac.userId) map[ac.userId] = teamName;
          }
        });
      }
    });

    return map;
  }, [teams]);

  const toggleRow = useCallback((id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  }, []);

  const handleSort = useCallback((key) => {
    if (sortKey === key) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }, [sortKey]);

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      let aVal, bVal;
      switch (sortKey) {
        case 'name':
          aVal = (a.user?.name || a.user?.email || '').toLowerCase();
          bVal = (b.user?.name || b.user?.email || '').toLowerCase();
          break;
        case 'type': aVal = a.type; bVal = b.type; break;
        case 'role': aVal = a.role || ''; bVal = b.role || ''; break;
        case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
        case 'date':
          aVal = new Date(a.joinedAt || 0).getTime();
          bVal = new Date(b.joinedAt || 0).getTime();
          break;
        default: return 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDir]);

  const getDisplayName = (person) => {
    const user = person.user;
    if (!user) return 'Unknown User';
    if (user.name && user.name !== 'Development User') return user.name;
    if (user.firstName || user.lastName) return [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || 'Unknown User';
    if (user.email) return user.email;
    if (user._id) return `User ${user._id.slice(-6)}`;
    return 'Unknown User';
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) return <FaSort className={styles.sortIcon} />;
    return sortDir === 'asc' ? <FaSortUp className={styles.sortIconActive} /> : <FaSortDown className={styles.sortIconActive} />;
  };

  const handleAction = (action, person) => {
    const userId = person.user?._id || person.id;
    const userName = getDisplayName(person);
    
    switch (action) {
      case 'changeRole': setRoleModalUser(person); break;
      case 'changeStatus': setStatusModalUser(person); break;
      case 'withdraw': onWithdraw?.(userId, userName); break;
      case 'suspend': onSuspend?.(userId, userName); break;
      case 'remove': onRemove?.(userId, userName); break;
      case 'approve': onApprove?.(person._original); break;
      case 'decline': onDecline?.(person._original); break;
      case 'allowApplication': onAllowApplication?.(userId, userName); break;
      case 'directApproval': onDirectApproval?.(userId, userName); break;
      case 'invite': onChangeStatus?.(userId, 'Applied'); break;
      default: break;
    }
  };

  const TypeBadge = ({ type }) => {
    const config = TYPE_CONFIG[type] || { label: type, icon: '❓', color: '#6b7280' };
    return (
      <span className={styles.typeBadge} style={{ '--type-color': config.color }}>
        {config.icon}
      </span>
    );
  };

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search people..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filters}>
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            className={styles.filterSelect}
          >
            <option value="all">All ({counts.total || 0})</option>
            <option value="members">Members ({counts.members || 0})</option>
            <option value="staff">Staff ({counts.staff || 0})</option>
            <option value="applicants">Applicants ({counts.applicants || 0})</option>
            <option value="waitlist">Waitlist ({counts.waitlist || 0})</option>
            <option value="followers">Followers ({counts.followers || 0})</option>
          </select>

          {filterOptions.roles?.length > 0 && (
            <select
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              className={styles.filterSelect}
            >
              <option value="">All Roles</option>
              {filterOptions.roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          )}

          {filterOptions.statuses?.length > 0 && (
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className={styles.filterSelect}
            >
              <option value="">All Statuses</option>
              {filterOptions.statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          )}
        </div>

        <button className={styles.refreshBtn} onClick={onRefresh} disabled={isLoading}>
          {isLoading ? '⏳' : '🔄'} Refresh
        </button>
      </div>

      {/* Bulk Actions */}
      {selectedCount > 0 && (
        <div className={styles.bulkBar}>
          <span>{selectedCount} selected</span>
          <div className={styles.bulkActions}>
            <button onClick={clearSelection}>Clear</button>
            <button className={styles.bulkPrimary}><FaEnvelope /> Message</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkCol}>
                <input
                  type="checkbox"
                  checked={selectedCount === sortedData.length && sortedData.length > 0}
                  onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
                />
              </th>
              <th className={styles.expandCol}></th>
              <th onClick={() => handleSort('name')} className={styles.sortable}>
                Name {renderSortIcon('name')}
              </th>
              <th onClick={() => handleSort('type')} className={styles.sortable}>
                Type {renderSortIcon('type')}
              </th>
              <th onClick={() => handleSort('role')} className={styles.sortable}>
                Role {renderSortIcon('role')}
              </th>
              <th onClick={() => handleSort('status')} className={styles.sortable}>
                Status {renderSortIcon('status')}
              </th>
              <th>Team</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && sortedData.length === 0 ? (
              <tr><td colSpan="7" className={styles.centerCell}>Loading...</td></tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan="7" className={styles.centerCell}>
                  <div className={styles.emptyState}>
                    <span>👥</span>
                    <p>No people found</p>
                    <button onClick={() => setFilters({ type: 'all', search: '', role: '', status: '' })}>
                      Clear Filters
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map(person => (
                <React.Fragment key={person.uniqueKey}>
                  <tr className={`${styles.row} ${isSelected(person.id) ? styles.selected : ''}`}>
                    <td className={styles.checkCol}>
                      <input
                        type="checkbox"
                        checked={isSelected(person.id)}
                        onChange={() => toggleSelection(person.id)}
                      />
                    </td>
                    <td className={styles.expandCol}>
                      <button className={styles.expandBtn} onClick={() => toggleRow(person.uniqueKey)}>
                        {expandedRows.has(person.uniqueKey) ? <FaChevronDown /> : <FaChevronRight />}
                      </button>
                    </td>
                    <td>
                      <div className={styles.nameCell}>
                        <div className={styles.avatar} style={{ '--type-color': TYPE_CONFIG[person.type]?.color || '#6b7280' }}>
                          {(getDisplayName(person)[0] || '?').toUpperCase()}
                        </div>
                        <div className={styles.nameInfo}>
                          <span className={styles.name}>{getDisplayName(person)}</span>
                          <span className={styles.email}>{person.user?.email}</span>
                        </div>
                      </div>
                    </td>
                    <td><TypeBadge type={person.type} /></td>
                    <td>
                      {person.type === 'club-management' ? (
                        <ClubRoleBadge role={person.role} />
                      ) : person.role ? (
                        <RoleBadge role={person.role} />
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {person.status ? <StatusPill status={person.status} /> : <span className={styles.muted}>—</span>}
                    </td>
                    <td>
                      {(() => {
                        const userId = person.user?._id || person.id;
                        const assignedTeam = memberTeamMap[userId];
                        if (assignedTeam) {
                          return (
                            <span className={styles.teamBadge}>
                              <FaShieldAlt size={9} />
                              {assignedTeam}
                            </span>
                          );
                        }
                        if (person.type === 'member' || person.type === 'staff') {
                          return <span className={styles.noTeamBadge}>No Team</span>;
                        }
                        return <span className={styles.muted}>—</span>;
                      })()}
                    </td>
                    <td className={styles.actionsCell}>
                      <QuickActions person={person} onAction={handleAction} isRegistrationEnabled={isRegistrationEnabled} />
                      <ActionMenu person={person} onAction={handleAction} isRegistrationEnabled={isRegistrationEnabled} />
                    </td>
                  </tr>
                  {expandedRows.has(person.uniqueKey) && (
                    <tr className={styles.expandedRow}>
                      <td colSpan="8">
                        <div className={styles.expandedContent}>
                          <div className={styles.detailGrid}>
                            <div><label>Email</label><span>{person.user?.email || '—'}</span></div>
                            <div><label>User ID</label><span className={styles.mono}>{person.user?._id || person.id}</span></div>
                            <div><label>Joined</label><span>{person.joinedAt ? new Date(person.joinedAt).toLocaleDateString() : '—'}</span></div>
                            {person.teamId && <div><label>Team</label><span>{person.teamId}</span></div>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        Showing {sortedData.length} of {counts.total || 0} people
      </div>

      {/* Role Change Modal */}
      {roleModalUser && (
        <div className={styles.modalOverlay} onClick={() => setRoleModalUser(null)}>
          <div className={styles.modalLarge} onClick={e => e.stopPropagation()}>
            <h3>Change Role</h3>
            <p>Select a new role for {getDisplayName(roleModalUser)}</p>
            <div className={styles.roleCategories}>
              {CATEGORY_ORDER.map(category => {
                const categoryInfo = ROLE_CATEGORIES[category];
                const roles = getRolesByCategory(category);
                return (
                  <div key={category} className={styles.roleCategory}>
                    <div className={styles.categoryHeader} style={{ '--cat-color': categoryInfo.color }}>
                      <span className={styles.categoryIcon}>{categoryInfo.icon}</span>
                      <span>{categoryInfo.label}</span>
                    </div>
                    <div className={styles.categoryRoles}>
                      {roles.map(roleKey => {
                        const roleInfo = ROLE_DEFINITIONS[roleKey];
                        const isActive = roleModalUser.role === roleKey;
                        return (
                          <button
                            key={roleKey}
                            className={`${styles.roleOption} ${isActive ? styles.roleActive : ''}`}
                            style={{ '--role-color': categoryInfo.color }}
                            onClick={() => {
                              onChangeRole?.(roleModalUser.user?._id || roleModalUser.id, roleKey);
                              setRoleModalUser(null);
                            }}
                          >
                            <span className={styles.roleName}>{roleInfo.display}</span>
                            <span className={styles.roleDesc}>{roleInfo.description}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <button className={styles.modalClose} onClick={() => setRoleModalUser(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {statusModalUser && (
        <div className={styles.modalOverlay} onClick={() => setStatusModalUser(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3>Change Status</h3>
            <p>Select a new status for {getDisplayName(statusModalUser)}</p>
            <div className={styles.statusGrid}>
              {['Enrolled', 'Pending', 'Suspended', 'Withdrawn'].map(status => (
                <button
                  key={status}
                  className={`${styles.statusOption} ${statusModalUser.status === status ? styles.statusActive : ''}`}
                  onClick={() => {
                    onChangeStatus?.(statusModalUser.user?._id || statusModalUser.id, status);
                    setStatusModalUser(null);
                  }}
                >
                  {status}
                </button>
              ))}
            </div>
            <button className={styles.modalClose} onClick={() => setStatusModalUser(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedPeopleTable;

